import { useLazyQuery } from '@apollo/client'
import DropdownBase, { Option } from 'components/partials/dropDown'
import { TabProps } from 'components/partials/tabs'
import { notify } from 'components/toastify'
import { STATUS } from 'constants/messages'
import {
  AgencyGroupJobTypeEnum,
  GET_ALL_AGENCY_GROUP,
  GET_DETAIL_AGENCY_GROUP_BY_ID,
} from 'queries/admin/agencyGroup'
import React, { useEffect, useState } from 'react'
import { IAgencyGroup, IAgencyGroupJobConfig } from '../interface'
import AddNewForwardWebhook from './components/addNewForwardWebhook'
import EditForwardWebhookConfig from './components/editForwardWebhookConfig'
import IconInformation from 'assets/images/icon-information.svg'
import AgencyActivityTable from './components/activity-table'
import { useSession } from 'helpers/checkSession'

interface Props extends TabProps {
  dataInput: {
    currentAgencyGroup: IAgencyGroup
  }
}

interface IDropdownAgencyGroupOption extends Option {}

const ForwardWebhook: React.FC<Props> = ({ dataInput }) => {
  const { handleErrorSession } = useSession()
  const [currentAgencyGroup, setCurrentAgencyGroup] = useState(dataInput.currentAgencyGroup)
  const [agencyGroupJobConfigs, setAgencyGroupJobConfigs] = useState<IAgencyGroupJobConfig | null>(
    null,
  )
  const [dropdownAgencyGroupOptions, setDropdownAgencyGroupOptions] = useState<
    IDropdownAgencyGroupOption[]
  >([])
  const [webhookForwardData, setWebhookForwardData] = useState<Record<string, any> | null>(null)

  const [getAllAgencyGroupQuery] = useLazyQuery(GET_ALL_AGENCY_GROUP, {
    variables: { skip: 0, take: 50 },
    fetchPolicy: 'no-cache',
    onCompleted: (data) => {
      if (!data) return
      const options: IDropdownAgencyGroupOption[] = data.getAllAgencyGroup.map((group) => ({
        id: group.agencyGroupId,
        name: group.agencyGroupName,
      }))
      setDropdownAgencyGroupOptions(options)
    },
    onError: (error) => {
      if (error.message) {
        handleErrorSession(error, error.message)
      } else {
        notify('Something wrong', STATUS.ERROR)
      }
    },
  })

  const [getDetailAgencyGroupByIdQuery, { refetch: refetchDetailData }] = useLazyQuery(
    GET_DETAIL_AGENCY_GROUP_BY_ID,
    {
      fetchPolicy: 'no-cache',
      onCompleted: (data) => {
        const config = data?.getDetailAgencyGroupById?.[0]
        if (!config) {
          setAgencyGroupJobConfigs(null)
          return
        }

        const rawHeaders = config.forwardWebhook?.headers ?? {}

        const headersArray = Object.entries(rawHeaders).map(([key, value]) => ({
          key,
          value: String(value),
        }))

        const formattedData = {
          ...config.forwardWebhook,
          headers: headersArray,
        }

        setAgencyGroupJobConfigs(config)
        setWebhookForwardData(formattedData)
      },
      onError: (error) => {
        if (error.message) {
          handleErrorSession(error, error.message)
        } else {
          notify('Something wrong', STATUS.ERROR)
        }
      },
    },
  )

  const onSelectAgencyGroupOption = (option: IDropdownAgencyGroupOption) => {
    if (currentAgencyGroup?.agencyGroupId === option.id || typeof option.id !== 'number') return
    setCurrentAgencyGroup({
      agencyGroupId: option.id,
      agencyGroupName: option.name,
      agencies: [],
      agencyGroupJobConfigs: [],
    })
  }

  useEffect(() => {
    getAllAgencyGroupQuery()
  }, [])

  useEffect(() => {
    if (currentAgencyGroup?.agencyGroupId) {
      getDetailAgencyGroupByIdQuery({
        variables: {
          agencyGroupId: currentAgencyGroup.agencyGroupId,
          customJobType: AgencyGroupJobTypeEnum.FORWARD_WEBHOOK,
        },
      })
    }
  }, [currentAgencyGroup?.agencyGroupId])

  return (
    <div className="px-20 md:px-10 space-y-2">
      <div className="pb-5 flex">
        <img className="h-5" src={IconInformation} alt="Icon-Information" />
        <span className="italic text-sm">Config webhook to forwarding</span>
      </div>
      <div className="w-full">
        <span>
          <strong>Agency Group</strong>
        </span>
        <div className="flex w-full space-x-2">
          <DropdownBase
            className="w-full"
            options={dropdownAgencyGroupOptions}
            initialValue={currentAgencyGroup?.agencyGroupName ?? ''}
            handleOptions={(option) =>
              onSelectAgencyGroupOption(option as IDropdownAgencyGroupOption)
            }
            placeHolder="Select agency group to add or edit"
          />
        </div>
      </div>

      {/*** Webhook Forward Form **/}
      {webhookForwardData && agencyGroupJobConfigs ? (
        <EditForwardWebhookConfig
          currentAgencyGroup={currentAgencyGroup}
          agencyGroupJobConfigs={agencyGroupJobConfigs}
          webhookForwardData={webhookForwardData}
          refetchData={refetchDetailData}
        />
      ) : (
        <AddNewForwardWebhook
          currentAgencyGroup={currentAgencyGroup}
          refetchData={refetchDetailData}
        />
      )}

      {currentAgencyGroup && (
        <div className="py-10">
          <span>
            <strong>Activity Log</strong>
          </span>
          <AgencyActivityTable
            activityType={'FORWARD_WEBHOOK'}
            currentAgencyGroup={currentAgencyGroup}
            handleFetchMore={() => {}}
            handleRefetch={() => {}}
            hasMoreData={false}
            isLoading={false}
          />
        </div>
      )}
    </div>
  )
}

export default ForwardWebhook
